/*
 * Copyright (c) 2022 by N-Ach (Pvt) Ltd
 *
 * ╔═╗ ╔╗    ╔═══╗  ╔╗
 * ║║╚╗║║    ║╔═╗║  ║║
 * ║╔╗╚╝║    ║║ ║╠══╣╚═╗
 * ║║╚╗║║╔══╗║╚═╝║╔═╣╔╗║
 * ║║ ║║║╚══╝║╔═╗║╚═╣║║║
 * ╚╝ ╚═╝    ╚╝ ╚╩══╩╝╚╝
 *
 * All rights reserved.
 *
 * This software is the confidential and proprietary information
 * of N-Ach (Pvt) Ltd. You shall not disclose such Confidential
 * Information and shall use it only in accordance with the terms
 * of the license agreement you entered into with N-Ach (Pvt) Ltd.
 *
 *
 * @Author Dilhari Jayathunga
 * @Date 25/10/2022
 */

import { apiSlice } from "../Slices/apiSlice";

const ORIGIN_SYSTEM = 2;
const ORDER_STATUS_ID = 5;

export const orderService = apiSlice.injectEndpoints({
    endpoints: (builder) => ({
        getAllOrders: builder.query({
            query: () => ({
                url: `/orderHist-management/managed-orderHist/getByStatus/${ORDER_STATUS_ID}`,
                params: {
                    originSystem: ORIGIN_SYSTEM,
                },
            }),
            providesTags: ["myOrders"],
        }),

        getAllConfirmedOrders: builder.query({
            query: () => ({
                url: "/orderHist-management/managed-orderHist/customer",
                params: {
                    originSystem: ORIGIN_SYSTEM,
                },
            }),
            providesTags: ["myOrders"],
        }),

        getOrdersByStatus: builder.query({
            query: (args) => {
                const payload = typeof args === "object" && args !== null ? args : { statusId: args };
                const { statusId, startDate, endDate } = payload;

                return {
                    url: `/orderHist-management/managed-orderHist/getByStatus/${statusId}`,
                    params: {
                        originSystem: ORIGIN_SYSTEM,
                        ...(startDate ? { startDate } : {}),
                        ...(endDate ? { endDate } : {}),
                    },
                };
            },
            providesTags: ["myOrders"],
        }),

        getAllReturnStatus: builder.query({
            query: () => "/orderStatus-management/managed-rejectStatus",
        }),

        getReturnedOrdersByStatus: builder.query({
            query: (statusId) => ({
                url: `/orderDet-management/managed-orderDet/reject-status/${statusId}`,
                params: {
                    originSystem: ORIGIN_SYSTEM,
                },
            }),
            providesTags: ["myOrders"],
        }),

        getAllRejectReasons: builder.query({
            query: () => "/orderRejectReason-management/managed-orderRejectReason",
        }),

        getAllRefundReasons: builder.query({
            query: () => "/orderDet-management/managed-orderDet/refund-reason",
        }),

        refundOrder: builder.mutation({
            query: (remarks) => ({
                url: "/orderDet-management/managed-orderDet/refund",
                method: "PUT",
                body: remarks,
            }),
            invalidatesTags: ["myOrders"],
        }),

        reOrder: builder.mutation({
            query: (items) => ({
                url: "/orderHead-management/managed-orderHead/reorder",
                method: "PUT",
                body: items,
            }),
            invalidatesTags: ["cart"],
        }),

        overwriteOrder: builder.mutation({
            query: (items) => ({
                url: "/orderHead-management/managed-orderHead/reorder-overwrite",
                method: "PUT",
                body: items,
            }),
            invalidatesTags: ["cart"],
        }),

        orderApprove: builder.mutation({
            query: (orderDetails) => ({
                url: "/orderHist-management/managed-orderHist/status",
                method: "PUT",
                body: orderDetails,
            }),
            invalidatesTags: ["myOrders"],
        }),

        orderReject: builder.mutation({
            query: (orderDetails) => ({
                url: "/orderHist-management/managed-orderHist/reject",
                method: "PUT",
                body: orderDetails,
            }),
            invalidatesTags: ["myOrders"],
        }),

        updateRefundStatus: builder.mutation({
            query: (orderDetails) => ({
                url: `/orderDet-management/managed-orderDet/refund/${orderDetails?.orderDetId}`,
                method: "PUT",
                body: orderDetails,
            }),
            invalidatesTags: ["myOrders"],
        }),
    }),
});

// Export hooks for usage in functional components
export const {
    useGetAllOrdersQuery,
    useGetAllConfirmedOrdersQuery,
    useGetOrdersByStatusQuery,
    useGetAllReturnStatusQuery,
    useLazyGetReturnedOrdersByStatusQuery,
    useGetAllRejectReasonsQuery,
    useGetAllRefundReasonsQuery,
    useRefundOrderMutation,
    useReOrderMutation,
    useOverwriteOrderMutation,
    useOrderApproveMutation,
    useOrderRejectMutation,
    useUpdateRefundStatusMutation,
} = orderService;